'use client';

import React, { memo, useMemo, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { AlertCircle, DraftingCompass } from 'lucide-react';
import { PulsingDots } from '@/shared/ui';
import { Progress } from '@/shared/ui/shadcn/progress';
import { calculateTimeBasedProgress } from '@/shared/lib/render-status';
import { cn } from '@/shared/lib/cn';

type AlbumStatus = 'pending' | 'processing' | 'completed' | 'failed';

interface AlbumCoverViewProps {
  coverImageUrl?: string | null;
  conceptTitle?: string | null;
  conceptNumber: number;
  format: string;
  createdAt?: string | null;
  status: AlbumStatus;
  startedAt?: string | null;
  durationPredicted?: number | null;
  albumId?: string;
  albumTitle?: string | null;
  className?: string;
}

/**
 * Album cover design component matching Android AlbumCoverView.
 * Displays a teal container with white paper, spiral binding, and title block.
 */
export const AlbumCoverView = memo(function AlbumCoverView({
  coverImageUrl,
  conceptTitle,
  conceptNumber,
  format,
  createdAt,
  status,
  startedAt,
  durationPredicted,
  albumId,
  albumTitle,
  className,
}: AlbumCoverViewProps) {
  const t = useTranslations('render.queue');
  const tConcept = useTranslations('concept');

  // Header — always the localized "Facade design"
  // (Apple parity: AppleCoverView never passes a non-nil projectName so the
  // visible string is always the localized default).
  const headerText = useMemo(
    () => t('coverDefaultHeader').toUpperCase(),
    [t]
  );

  const subtitle = tConcept('conceptNumber', { number: conceptNumber });

  const mainTitle = useMemo(
    () =>
      (albumTitle && albumTitle.trim()) ||
      (conceptTitle && conceptTitle.trim()) ||
      subtitle,
    [albumTitle, conceptTitle, subtitle]
  );

  const dateText = useMemo(() => {
    if (!createdAt) return '';
    try {
      return new Date(createdAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  }, [createdAt]);

  // Auto-update tick: re-runs the time-based progress calc every 500ms while
  // a job is processing, so the overlay percentage advances even without a WS
  // progress event between server pings.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (status === 'processing' && startedAt) {
      const interval = setInterval(() => setTick((t) => t + 1), 500);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [status, startedAt]);

  // Calculate progress for processing state
  const progress = useMemo(() => {
    if (status !== 'processing') return 0;
    if (startedAt && durationPredicted) {
      return calculateTimeBasedProgress(startedAt, durationPredicted, albumId);
    }
    return 0;
  }, [status, startedAt, durationPredicted, albumId, tick]);

  return (
    <div
      className={cn(
        // Album paper now fills the whole card — no teal frame.
        // Corner radius mirrors Apple (`containerSize × 0.04`). Drop shadow
        // is intentionally restrained: a tight contact layer + a small
        // diffuse layer at low opacity, so the album reads as resting on
        // the surface rather than floating above it. Mirrors the calm
        // Apple `.shadow(.drop)` values (radius 4 / 2, opacity 0.10 / 0.24)
        // within CSS box-shadow semantics.
        // No `overflow-hidden` on the outer container: spiral binding rings
        // sit half outside the paper's left edge and must render past the
        // bounds. The inner paper div and the status overlay each carry
        // the same asymmetric rounded corners, so the visible paper still
        // reads as rounded while the rings can extend outward.
        '@container/cover relative w-full h-full rounded-tl-[3%_4%] rounded-tr-[3%_4%] rounded-bl-[3%_4%] rounded-br-[3%_4%] shadow-[0_1px_2px_rgba(0,0,0,0.18),0_8px_16px_rgba(0,0,0,0.08)]',
        status === 'processing' && 'ring-2 ring-primary/60 ring-offset-0',
        status === 'processing' && progress >= 95 && 'animate-pulse',
        className
      )}
    >
      {/* Paper fills the card edge-to-edge. */}
      <div className="absolute inset-0">
        <div className="@container/paper relative w-full h-full bg-white rounded-tl-[3%_4%] rounded-tr-[3%_4%] rounded-bl-[3%_4%] rounded-br-[3%_4%]">
          {/* Apple `AlbumCoverView` paper layout reproduced row-by-row.
              - Per-row asymmetric padding (10% left / 5% right) for the
                spiral binding, EXCEPT footer which spans full width.
              - All spacers and font sizes follow the Apple formulas.
              - Paper is 4:3, so containerSize ≈ paperHeight, and a
                value `containerSize × k` ≈ paperWidth × (k × 0.75)
                → use `cqi` (= 1% paperWidth) with the converted ratio. */}
          <div className="flex flex-col h-full">
            {/* paddingTop = albumHeight × 0.05 */}
            <div className="h-[5%] shrink-0" />

            {/* Header — uppercased projectName or default. Apple:
                font size = containerSize × 0.025 × 1.035 ≈ 2.46cqi,
                lineLimit(2), regular, secondary color.
                Capped (min …px) so the type stops growing on large detail
                cards — mirrors Apple `fontBasisCap` (basis 288 → 9.54px). */}
            <p className="pl-[10%] pr-[5%] text-center text-gray-500 font-normal leading-tight line-clamp-2 text-[min(2.46cqi,9.54px)]">
              {headerText}
            </p>

            <div className="h-[4%] shrink-0" />

            {/* Cover image — height = albumHeight × 0.47.
                Width fills the content area (between binding/right margins).
                Corner radius = imageHeight × 0.03 ≈ 1.06cqi.
                Apple uses contentMode .fill → object-cover. */}
            <div className="h-[47%] shrink-0 pl-[10%] pr-[5%]">
              {coverImageUrl ? (
                <div className="relative w-full h-full overflow-hidden rounded-[1.06cqi]">
                  <Image
                    src={coverImageUrl}
                    alt=""
                    fill
                    sizes="400px"
                    className={cn(
                      'object-cover',
                      status === 'failed' && 'saturate-[0.25] brightness-95'
                    )}
                  />
                </div>
              ) : (
                <div className="w-full h-full overflow-hidden rounded-[1.06cqi] bg-gray-100 flex flex-col items-center justify-center gap-[1cqi]">
                  <DraftingCompass className="w-[12%] h-[20%] text-gray-400" />
                  <span className="text-gray-400 text-[1.9cqi]">
                    {format}
                  </span>
                </div>
              )}
            </div>

            <div className="h-[3%] shrink-0" />

            {/* Main title — bold, primary color, lineLimit(2).
                Apple: containerSize × 0.045 × 1.035 ≈ 4.43cqi.
                Capped (min …px) so the type stops growing on large detail
                cards — mirrors Apple `fontBasisCap` (basis 288 → 17.00px). */}
            <p className="pl-[10%] pr-[5%] text-center text-gray-900 font-bold leading-tight line-clamp-2 text-[min(4.43cqi,17.00px)]">
              {mainTitle}
            </p>

            <div className="h-[1.5%] shrink-0" />

            {/* Subtitle (only when different from main title).
                Apple: containerSize × 0.03 × 1.035 ≈ 2.96cqi, lineLimit(1). */}
            {mainTitle !== subtitle && (
              <>
                <p className="pl-[10%] pr-[5%] text-center text-gray-500 leading-tight truncate text-[min(2.96cqi,11.33px)]">
                  {subtitle}
                </p>
                <div className="h-[2.5%] shrink-0" />
              </>
            )}

            {/* Flexible spacer pushes the footer to the bottom. */}
            <div className="flex-1" />

            {/* Footer — full paper width, NO binding padding (Apple HStack
                has no leading/trailing padding). gap = albumWidth × 0.02. */}
            <div className="flex items-center justify-center gap-[2cqi] leading-tight text-[min(2.46cqi,9.54px)]">
              <span className="text-[#078D86] font-medium">GetFacade.ai</span>
              {dateText && (
                <>
                  <span className="text-gray-500">&bull;</span>
                  <span className="text-gray-500">{dateText}</span>
                </>
              )}
            </div>

            {/* paddingBottom = albumHeight × 0.05 */}
            <div className="h-[5%] shrink-0" />
          </div>

          {/* Spiral binding — rings along left edge of paper */}
          <SpiralBinding />
        </div>
      </div>

      {/* Status overlay for non-completed albums */}
      {status !== 'completed' && (
        <div className="absolute inset-0 bg-[hsl(177,85%,12%)]/60 flex items-center justify-center rounded-tl-[3%_4%] rounded-tr-[3%_4%] rounded-bl-[3%_4%] rounded-br-[3%_4%]">
          <div className="flex flex-col items-center gap-2">
            {status === 'pending' && (
              <>
                <PulsingDots size="lg" className="text-white" />
                <span className="text-white text-list-item-value">
                  {t('queued')}
                </span>
              </>
            )}
            {status === 'processing' && (
              <>
                <div className="text-white text-screen-subtitle">
                  {Math.round(progress)}%
                </div>
                <Progress value={progress} className="w-16 h-1" />
              </>
            )}
            {status === 'failed' && (
              <>
                <AlertCircle className="w-8 h-8 text-white" />
                <span className="text-white text-list-item-value">
                  {t('failed')}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * Plain monotone binding rings centered on the paper's left edge —
 * half outside, half inside (notebook-rings metaphor).
 */
function SpiralBinding() {
  const ringCount = 12;

  return (
    <div className="absolute left-0 top-[5%] bottom-[5%] -translate-x-1/2 flex flex-col justify-between items-center">
      {Array.from({ length: ringCount }).map((_, i) => (
        <div
          key={i}
          className="w-[2.85cqi] h-[2.85cqi] rounded-full border-[max(1px,0.45cqi)] border-gray-400 dark:border-gray-500 bg-transparent"
        />
      ))}
    </div>
  );
}
